import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { TableLazyLoadEvent } from 'primeng/table';
import { Subject, Subscription, timer } from 'rxjs';
import { finalize, takeUntil, timeout } from 'rxjs/operators';
import { BaseServices } from '../../kuba.services';
import { FlexiAdvisorSettingsService } from './flexi-advisor-settings.service';
import {
  FlexiAdvisorAssignment,
  FlexiAdvisorAssignmentsMetadata,
  FlexiAdvisorCoverage,
  FlexiAdvisorWorkload,
} from './flexi-advisor-settings.models';
import { initials, primaryOf } from './flexi-advisor-settings.logic';

/** One filter chip above the table. */
interface CoverageFilter {
  value: FlexiAdvisorCoverage | null;
  labelKey: string;
  count: (metadata: FlexiAdvisorAssignmentsMetadata) => number;
}

const EMPTY_METADATA: FlexiAdvisorAssignmentsMetadata = {
  AllCount: 0,
  NoActiveAdvisorCount: 0,
  PrimaryInactiveCount: 0,
  Workload: [],
};

/**
 * Admin mode › Flexi › Settings: which HSE advisors each of the application's
 * active Flexi businesses has, and who each business actually sees.
 *
 * The backend decides everything that matters — which businesses are in reach,
 * who may be added, who the primary is — and this page shows it. Filters, the
 * workload panel and the counts on the chips cover the whole application, not
 * just the page.
 */
@Component({
  selector: 'flexi-advisor-settings',
  templateUrl: './flexi-advisor-settings.component.html',
  styleUrls: ['./flexi-advisor-settings.component.scss'],
  standalone: false,
})
export class FlexiAdvisorSettingsComponent implements OnInit, OnDestroy {
  readonly pageSize = 25;
  readonly initials = initials;
  readonly currentUserId = Number(BaseServices.UserId);

  readonly filters: CoverageFilter[] = [
    { value: null, labelKey: 'FLEXI_SETTINGS.FILTER_ALL', count: m => m.AllCount },
    { value: 'NoActiveAdvisor', labelKey: 'FLEXI_SETTINGS.FILTER_NO_ACTIVE', count: m => m.NoActiveAdvisorCount },
    { value: 'PrimaryInactive', labelKey: 'FLEXI_SETTINGS.FILTER_PRIMARY_INACTIVE', count: m => m.PrimaryInactiveCount },
  ];

  rows: FlexiAdvisorAssignment[] = [];
  metadata: FlexiAdvisorAssignmentsMetadata = EMPTY_METADATA;
  totalRecords = 0;
  first = 0;
  search = '';
  myBusinesses = true;
  primary: boolean | null = null;
  coverage: FlexiAdvisorCoverage | null = null;
  selected: FlexiAdvisorAssignment[] = [];
  loading = false;
  loadFailed = false;

  /** The business whose list is open in the edit dialog. */
  editing: FlexiAdvisorAssignment | null = null;
  bulkOpen = false;

  private readonly destroy$ = new Subject<void>();
  private pageLoad = new Subscription();
  private searchDelay = new Subscription();

  constructor(
    private api: FlexiAdvisorSettingsService,
    private translate: TranslateService,
    private changeDetector: ChangeDetectorRef,
  ) {}

  /** Active admins of the application: the advisors that can be added. */
  get selectableAdvisors(): FlexiAdvisorWorkload[] {
    return this.metadata.Workload.filter(advisor => advisor.IsSelectable);
  }

  /** The caller's totals across active Flexi businesses, independent of search and paging. */
  get myWorkload(): FlexiAdvisorWorkload | undefined {
    return this.metadata.Workload.find(advisor => advisor.UserId === this.currentUserId);
  }

  /** Listed assignments where another eligible advisor is primary. */
  get backupCount(): number {
    return (this.myWorkload?.AssignedCount ?? 0) - (this.myWorkload?.PrimaryCount ?? 0);
  }

  /** Loads the default personal assignment tab. */
  ngOnInit(): void {
    this.loadPage();
  }

  /** Cancels pending page requests and debounced searches. */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Loads the current scope and filters, refreshing rows and application-wide totals. */
  loadPage(): void {
    this.searchDelay.unsubscribe();
    this.pageLoad.unsubscribe();
    this.loading = true;
    this.loadFailed = false;
    this.pageLoad = this.api
      .assignments(
        Math.floor(this.first / this.pageSize) + 1, this.pageSize, this.search.trim(),
        this.coverage, this.myBusinesses, this.primary,
      )
      .pipe(
        timeout(30_000),
        takeUntil(this.destroy$),
        finalize(() => {
          this.loading = false;
          this.changeDetector.markForCheck();
        }),
      )
      .subscribe({
        next: page => {
          this.rows = page.Items;
          this.totalRecords = page.TotalCount;
          this.metadata = page.Metadata ?? EMPTY_METADATA;
        },
        error: () => {
          this.rows = [];
          this.totalRecords = 0;
          this.loadFailed = true;
        },
      });
  }

  /** Displays the requested page with the current scope and filters. */
  pageChanged(event: TableLazyLoadEvent): void {
    this.first = event.first ?? 0;
    this.loadPage();
  }

  /** Debounces name and organisation-number searches, starting again at page one. */
  searchChanged(): void {
    this.selected = [];
    this.searchDelay.unsubscribe();
    this.searchDelay = timer(300)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.first = 0;
        this.loadPage();
      });
  }

  /** Switches scope while preserving search and clearing filters, pagination and selection. */
  pickTab(myBusinesses: boolean): void {
    if (this.myBusinesses === myBusinesses) {
      return;
    }
    this.searchDelay.unsubscribe();
    this.myBusinesses = myBusinesses;
    this.coverage = null;
    this.primary = null;
    this.selected = [];
    this.rows = [];
    this.totalRecords = 0;
    this.first = 0;
    this.loadPage();
  }

  /** Filters the personal list by the caller's effective primary or backup role. */
  pickPrimary(primary: boolean | null): void {
    if (this.primary === primary) {
      return;
    }
    this.primary = primary;
    this.selected = [];
    this.first = 0;
    this.loadPage();
  }

  /** Applies a coverage filter within the all-businesses tab. */
  pickCoverage(coverage: FlexiAdvisorCoverage | null): void {
    if (this.coverage === coverage) {
      return;
    }
    this.coverage = coverage;
    this.selected = [];
    this.first = 0;
    this.loadPage();
  }

  /** Name of the advisor the business sees, if it has one. */
  shownName(row: FlexiAdvisorAssignment): string {
    return primaryOf(row.Advisors)?.Name ?? '';
  }

  /** Formats an advisor's application-wide assignment and primary counts. */
  workloadSummary(advisor: FlexiAdvisorWorkload): string {
    const activeKey = advisor.AssignedCount === 1
      ? 'FLEXI_SETTINGS.WORKLOAD_ACTIVE_ONE'
      : 'FLEXI_SETTINGS.WORKLOAD_ACTIVE';
    return advisor.IsActive
      ? this.translate.instant(activeKey, {
          assigned: advisor.AssignedCount,
          primary: advisor.PrimaryCount,
        })
      : this.translate.instant('FLEXI_SETTINGS.WORKLOAD_INACTIVE', { assigned: advisor.AssignedCount });
  }

  /** Opens the ordered advisor list for one business in either tab. */
  openEdit(row: FlexiAdvisorAssignment): void {
    this.editing = row;
  }

  /** A save changes the totals and the workload too, so the page is reloaded. */
  editClosed(saved: boolean): void {
    this.editing = null;
    if (saved) {
      this.selected = [];
      this.first = 0;
      this.loadPage();
    }
  }

  /** Opens bulk assignment for the current selection in either tab. */
  openBulk(): void {
    this.bulkOpen = this.selected.length > 0;
  }

  /** Refreshes assignments and totals after a successful bulk save. */
  bulkClosed(saved: boolean): void {
    this.bulkOpen = false;
    if (saved) {
      this.selected = [];
      this.first = 0;
      this.loadPage();
    }
  }
}
